import type { ExpectedCloseV2CompanyForecast } from "../expected-close-v2/localExpectedCloseForecast";
import { getExpectedCloseV2Timing } from "../forecast-diagnosis/forecastDiagnosisContract";
import { isWithinInterval } from "date-fns";

export type OutcomeGroup = "will" | "in-play" | "wont";
export type OutcomeReason = "delayed" | "low-win" | "terminal" | "low-period";
export type OutcomeDeal = {
  id: number;
  name: string;
  value: number;
  group: OutcomeGroup;
  reason: OutcomeReason | null;
  inPeriodProbability: number;
  horizonProbability: number;
  expectedCloseMonth: string | null;
  crmCloseDate: string;
};

/** Approved presentation thresholds on model probability mass, never on risk scores. */
export function groupForecastOutcomes(
  deals: readonly { id: number; name: string; value: number; closed_at?: string | Date | null; is_closed?: boolean; is_won?: boolean; is_lost?: boolean }[],
  artifact: ExpectedCloseV2CompanyForecast,
  months: ReadonlySet<string>,
  period: { start: Date; end: Date },
) {
  const predictions = new Map(artifact.dealForecasts.map(deal => [deal.dealId, deal]));
  const terminal = new Set(artifact.terminalBuyerOutcomeDeals?.map(deal => deal.dealId));
  const lastMonth = [...months].sort().at(-1) ?? "";
  const rows: OutcomeDeal[] = [];
  let unavailable = 0;
  for (const deal of deals) {
    if (deal.is_closed || deal.is_won || deal.is_lost) continue;
    // CRM defines the population; unchanged model timing defines its outcome.
    // Do not pull future-quarter or overdue CRM Deals into this period's counts.
    const crmCloseDate = deal.closed_at ? new Date(deal.closed_at) : null;
    if (!crmCloseDate || !Number.isFinite(crmCloseDate.getTime()) || !isWithinInterval(crmCloseDate, period)) continue;
    const prediction = predictions.get(Number(deal.id));
    if (!prediction && !terminal.has(Number(deal.id))) { unavailable++; continue; }
    if (prediction?.wonProbabilityByMonth.some(([, probability]) => !Number.isFinite(probability) || probability < 0 || probability > 1)) { unavailable++; continue; }
    const timing = getExpectedCloseV2Timing(prediction, months, lastMonth);
    const isTerminal = terminal.has(Number(deal.id));
    const horizon = isTerminal ? 0 : Math.min(1, prediction!.wonProbabilityByMonth.reduce((sum, [, p]) => sum + Math.max(0, Math.min(1, p)), 0));
    const within = isTerminal ? 0 : timing?.inPeriodProbability ?? 0;
    const group = within > .9 + 1e-10 ? "will" : within < .1 - 1e-10 ? "wont" : "in-play";
    const reason = group !== "wont" ? null : isTerminal ? "terminal"
      : horizon < .1 - 1e-10 ? "low-win"
        : horizon > .1 + 1e-10 && timing?.expectedAfterPeriod ? "delayed" : "low-period";
    rows.push({ id: Number(deal.id), name: deal.name, value: Number(deal.value), group,
      reason, inPeriodProbability: within, horizonProbability: horizon,
      expectedCloseMonth: timing?.expectedCloseMonth ?? null, crmCloseDate: crmCloseDate.toISOString() });
  }
  return { rows: rows.toSorted((a, b) => b.value - a.value), unavailable };
}
